/**
 * World — the build context every kit module writes into.
 * Owns the static batcher, the instanced prop library, collision proxies,
 * surface zones and the light hints handed to the lighting team.
 *
 * owner: level
 */
import * as THREE from 'three';
import { Batcher, InstanceLib, plainBox, xform, mergeGeometries, stripAttrs } from '../Geo.js';

export class World {
  constructor(rng) {
    this.rng = rng;
    this.batch = new Batcher(rng);
    this.props = new InstanceLib();
    this.lightHints = [];
    this.zones = [];            // { x0,z0,x1,z1, y, surface }  ground surface lookup
    this._colGeos = new Map();  // surface -> geometry[]
    this.colBoxes = [];         // { box: Box3, surface }  used by surfaceAt
    this.navBlockers = [];
    /*
     * Every horizontal cloth/board that hangs over the player's head — awnings,
     * stall canopies, the souk cloth run — registers its UNDERSIDE quad here so
     * `ctx.level.verifyOverheads()` can prove, by raycast rather than by eye,
     * that something is actually holding it up. A canopy reported as "re-seated"
     * without this check has been wrong once already.
     */
    this.overheads = [];
    /*
     * Heads of every service pole planted by the roofline pass. `linkCables`
     * strings catenaries between them once the whole map is built, which is how
     * the sky gets a web of wires across it instead of three hand-placed runs.
     */
    this.cableAnchors = [];
  }

  /**
   * Register an overhead panel for the support self-test.
   * @param {string} kind
   * @param {number[][]} corners  four world-space points on the panel UNDERSIDE
   * @param {object} opts
   *   root  { p:[x,y,z], dir:[x,y,z] } horizontal probe that must hit a wall
   *   drop  how far below the panel a support may be and still count (m)
   *   reach how far a point may be from a supported point / the wall (m)
   */
  overhead(kind, corners, opts = {}) {
    this.overheads.push({
      kind, corners,
      root: opts.root || null,
      drop: opts.drop ?? 0.65,
      reach: opts.reach ?? 1.9,
    });
    return this;
  }

  /* ---- collision ---------------------------------------------------- */

  /**
   * A solid blocker. Centre + size, optional Y rotation.
   * Also feeds the contact-dirt distance field unless `occlude:false`.
   */
  blocker(cx, cy, cz, w, h, d, rotY = 0, surface = 'concrete', occlude = true) {
    const g = plainBox(w, h, d);
    const m = xform([cx, cy, cz], rotY);
    g.applyMatrix4(m);
    let list = this._colGeos.get(surface);
    if (!list) { list = []; this._colGeos.set(surface, list); }
    list.push(stripAttrs(g));
    const c = Math.abs(Math.cos(rotY)), s = Math.abs(Math.sin(rotY));
    const ew = (w * c + d * s) / 2, ed = (w * s + d * c) / 2;
    this.colBoxes.push({
      x0: cx - ew, x1: cx + ew, z0: cz - ed, z1: cz + ed,
      y0: cy - h / 2, y1: cy + h / 2, surface,
    });
    if (occlude && h > 0.35) this.batch.occluder(cx - ew, cz - ed, cx + ew, cz + ed, h);
    return this;
  }

  /** Axis-aligned blocker from extents. */
  blockerBox(x0, y0, z0, x1, y1, z1, surface = 'concrete', occlude = true) {
    return this.blocker((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2,
      Math.abs(x1 - x0), Math.abs(y1 - y0), Math.abs(z1 - z0), 0, surface, occlude);
  }

  /* ---- surfaces ----------------------------------------------------- */

  /** Ground/floor surface region used by surfaceAt(). */
  zone(x0, z0, x1, z1, y, surface) {
    this.zones.push({
      x0: Math.min(x0, x1), x1: Math.max(x0, x1),
      z0: Math.min(z0, z1), z1: Math.max(z0, z1), y, surface,
    });
    return this;
  }

  /* ---- lighting hand-off -------------------------------------------- */

  light(position, color, intensity, radius, type = 'point', extra = {}) {
    this.lightHints.push({
      position: position.isVector3 ? position : new THREE.Vector3(position[0], position[1], position[2]),
      color: typeof color === 'number' ? new THREE.Color(color) : color,
      intensity, radius, type, ...extra,
    });
    return this;
  }

  /* ---- build -------------------------------------------------------- */

  buildCollision(root) {
    const out = [];
    for (const [surface, list] of this._colGeos) {
      if (!list.length) continue;
      let g;
      try { g = mergeGeometries(list, false); } catch (e) { console.warn('[level] collision merge', e); continue; }
      if (!g) continue;
      const mesh = new THREE.Mesh(g, new THREE.MeshBasicMaterial({ visible: false }));
      mesh.name = `collision:${surface}`;
      mesh.visible = false;
      mesh.userData.surface = surface;
      mesh.userData.collisionProxy = true;
      mesh.userData.physicsMask = 1;
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      root.add(mesh);
      out.push(mesh);
      list.forEach((x) => x.dispose());
    }
    return out;
  }

  surfaceAt(p) {
    const x = p.x ?? p[0], y = p.y ?? p[1], z = p.z ?? p[2];
    // topmost solid whose top face is at or just below the query point
    let best = null, bestY = -1e9;
    for (const b of this.colBoxes) {
      if (x < b.x0 - 0.05 || x > b.x1 + 0.05 || z < b.z0 - 0.05 || z > b.z1 + 0.05) continue;
      if (b.y1 > y + 0.35) continue;
      if (b.y1 > bestY) { bestY = b.y1; best = b; }
    }
    let zone = null, zoneY = -1e9;
    for (const s of this.zones) {
      if (x < s.x0 || x > s.x1 || z < s.z0 || z > s.z1) continue;
      if (s.y > y + 0.35) continue;
      if (s.y > zoneY) { zoneY = s.y; zone = s; }
    }
    if (zone && zoneY >= bestY - 0.02) return zone.surface;
    return best ? best.surface : 'sand';
  }
}
